import {Component, OnInit, ViewChild} from '@angular/core';
import {Observable} from 'rxjs';
import {Settings} from '@orga-app/settings';

import * as actionsUi from './dokumente.actions.ui';
import * as fromDokumentePage from './dokumente.reducer';
import {select, Store} from '@ngrx/store';
import {DocumentViewer, DocumentViewerOptions} from '@ionic-native/document-viewer/ngx';
import {DocumentService} from '@orga-app/providers/document.service';
import {WindowRefService} from '@orga-app/providers/window.service';
import {File} from '@ionic-native/file/ngx';
import {NotificationService} from '@orga-app/providers/notification.service';
import {SettingsProvider} from '@orga-app/settings/settings.service';
import {IonRefresher, LoadingController, Platform} from '@ionic/angular';
import {takeWhile} from 'rxjs/operators';
import {FileOpener} from '@ionic-native/file-opener/ngx';
import {SafeUrl} from '@angular/platform-browser';
import {ToggleDokumenteGroupAction} from './dokumente.actions.ui';
import {Dokument, Veranstaltung} from '../../../../clients/veranstaltungen';
import {getAktuelleVeranstaltung, GlobalAppState} from '../../app-state/app-state.reducer';


@Component({
    selector: 'app-dokumente',
    templateUrl: './dokumente.page.html',
})
export class DokumentePage implements OnInit {
    @ViewChild(IonRefresher, { static: false})
    refresher: IonRefresher;

    public settings$: Observable<Settings>;
    public themeDefault$: Observable<Settings>;
    logoHeaderSVG$: Observable<SafeUrl>;
    private aiName = 'DokumentePage';

    public aktuelleVeranstaltung$: Observable<Veranstaltung>;
    public dokumente$: Observable<Array<Dokument>>;
    public spieltagsDokumente$: Observable<Array<{key: string, value: Array<Dokument>, open: boolean}>>;
    public allgemeineDokumente$: Observable<Array<{key: string, value: Array<Dokument>, open: boolean}>>;
    public uiZeigeVeranstaltungDokumentLaden$: Observable<boolean>;
    public uiZeigeVeranstaltungDokumentLadefehler$: Observable<boolean>;
    public anzahlSpieltagDokumente$: Observable<number>;
    public anzahlAllgemeineDokumente$: Observable<number>;
    public filterString: string;

    private alive = true;
    private windowLocal: Window;
    private aktuelleVeranstaltungId: string;

    constructor(
        public platform: Platform,
        public file: File,
        public loadingCtrl: LoadingController,
        private notificationService: NotificationService,
        private windowRefService: WindowRefService,
        private store: Store<fromDokumentePage.PageState>,
        private storeVeranstaltung: Store<GlobalAppState>,
        private documentService: DocumentService,
        private documentViewer: DocumentViewer,
        private fileOpener: FileOpener,
        private settingsProvider: SettingsProvider,
    ) {
        this.themeDefault$ = this.settingsProvider.getThemeDefault();
        this.logoHeaderSVG$ = this.settingsProvider.getLogoHeaderSVG();
        this.settings$ = this.settingsProvider.getActiveTheme();
    }

    ngOnInit() {
        this.windowLocal = this.windowRefService.nativeWindow;

        this.aktuelleVeranstaltung$ = this.storeVeranstaltung.pipe(select(getAktuelleVeranstaltung));
        this.dokumente$ = this.store.pipe(select(fromDokumentePage.getDokumente));
        this.spieltagsDokumente$ = this.store.pipe(select(fromDokumentePage.getSpieltagsDokumente));
        this.allgemeineDokumente$ = this.store.pipe(select(fromDokumentePage.getAllgemeineDokumente));
        this.uiZeigeVeranstaltungDokumentLaden$ = this.store.pipe(select(fromDokumentePage.getUiZeigeVeranstaltungDokumenteLaden));
        this.uiZeigeVeranstaltungDokumentLadefehler$ = this.store.pipe(select(fromDokumentePage.getUiZeigeVeranstaltungDokumenteLadefehler));
        this.anzahlSpieltagDokumente$ = this.store.pipe(select(fromDokumentePage.getAnzahlSpieltagDokumente));
        this.anzahlAllgemeineDokumente$ = this.store.pipe(select(fromDokumentePage.getAnzahlAllgemeineDokumente));

        this.aktuelleVeranstaltung$.subscribe((aktuelleVeranstaltung) => {
            if (aktuelleVeranstaltung != null) {
                this.aktuelleVeranstaltungId = aktuelleVeranstaltung.veranstaltung_id;
            }
        });
        this.dokumenteErneutLaden();
    }

    ionViewWillEnter() {
        this.viewWillEnterPage().then();
    }

    async viewWillEnterPage() {
        this.store.dispatch(new actionsUi.PageStateResetAction());
        await this.dokumenteErneutLaden();
    }

    ionViewDidEnter() {
      if (this.refresher !== undefined) {
        this.refresher.disabled = false;
      }
    }

    ionViewWillLeave() {
      if (this.refresher !== undefined) {
        this.refresher.disabled = true;
      }
    }

    async dokumentAnzeigen(dokument: Dokument) {
        const loading = await this.loadingCtrl.create({
            spinner: 'lines',
            message: 'Dokument wird geladen...',
            translucent: true,
            backdropDismiss: false,
            keyboardClose: true
        });
        await loading.present();

        const url = '/api/query/veranstaltungen/' + dokument.veranstaltung_id + '/dokumente/' + dokument.dokument_id + '?uploadId=' + dokument.dokument_upload_id;
        this.documentService.getDocument(url).pipe(
            takeWhile(() => this.alive)
        ).subscribe(async (document) =>  {
                if (document != null) {
                    await loading.dismiss();
                    this.documentService.saveFile(document, this.replaceSpecialCharacter(dokument.titel) + '.pdf');
                } else {
                    await loading.dismiss();
                }
            },
            async (error) => {
                await this.notificationService.showMessageWithOk('Die Datei konnte nicht geladen werden.');
                await loading.dismiss();
            }, () => {
            });
    }

    async writeFile(dokument, document) {
        const options: DocumentViewerOptions = {
            title: dokument.titel,
            documentView: {closeLabel: 'Schließen'},
            openWith: {enabled: true},
            email: {enabled: true}
        };

        try {
          const x = await this.file.writeFile(this.file.dataDirectory, this.replaceSpecialCharacter(dokument.titel) + '.pdf', document);

          if (this.platform.is('ios')) {
            const cacheDirectory = this.file.dataDirectory.replace('file://', '');
            this.documentViewer.viewDocument(cacheDirectory + this.replaceSpecialCharacter(dokument.titel) + '.pdf', 'application/pdf', options, this.onShowDocument, this.onCloseDocument, this.onMissingApp, this.onErrorDocument);
          } else {

            try {
              await this.fileOpener.open(this.file.dataDirectory + this.replaceSpecialCharacter(dokument.titel) + '.pdf', 'application/pdf');
              console.debug('File is opened');
            } catch (error) {
              console.debug('Error opening file', error);
              this.documentViewer.viewDocument(this.file.dataDirectory + this.replaceSpecialCharacter(dokument.titel) + '.pdf', 'application/pdf', options, this.onShowDocument, this.onCloseDocument, this.onMissingApp, this.onErrorDocument);
            }

          }

        } catch (error) {
          console.error(error);
          this.notificationService.showMessageWithOk('Die Datei konnte nicht geladen werden.');
        }

    }

    async onShowDocument() {
    }

    async onCloseDocument() {
    }

    async onMissingApp(appId, installer) {
        if (confirm('Um das PDF ansehen zu können wird die kostenfreie PDF Viewer App benötigt. Jetzt installieren?')) {
            installer();
        }
    }

    async onErrorDocument(error) {
        window.console.error(error);
        this.notificationService.showMessageWithOk('Die Datei konnte nicht geladen werden.');
    }

     pageAktualisieren(refresher) {
        this.dokumenteErneutLaden();
        setTimeout(() => {
            console.log('Async operation has ended');
            // refresher.complete();
            refresher.target.complete();
        }, 200);
    }

    async dokumenteErneutLaden() {
        this.store.dispatch(new actionsUi.PageLoadDataAction());
    }

    replaceSpecialCharacter(value: string) {
        return value
            .replace(/ /g, '_')
            .replace(/ä/g, 'ae')
            .replace(/Ä/g, 'Ae')
            .replace(/ü/g, 'ue')
            .replace(/Ü/g, 'Ue')
            .replace(/ö/g, 'oe')
            .replace(/Ö/g, 'Oe')
            .replace(/ß/g, 'ss')
            .replace(/\//g, '-')
            .replace(/\\/g, '-')
            ;
    }

    async dokumentSuchen(filterString: string) {
        this.store.dispatch(new actionsUi.DokumentSuchenAction(filterString));
    }

    async toggleDokumenteGroup(groupKey) {
        this.store.dispatch(new ToggleDokumenteGroupAction(groupKey));
    }
}
